import { useState, useEffect, FormEvent, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../../api/client';
import Logo from '../../components/Logo';
import StoreMap from '../../components/StoreMap';
import { MapPin, ExternalLink } from 'lucide-react';

interface ParticipatingStore {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
}

// Confetti component
function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffa500', '#ff69b4'];

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      color: string;
      size: number;
      rotation: number;
      rotationSpeed: number;
      shape: 'rect' | 'circle';
    }

    const particles: Particle[] = [];

    // Create particles
    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        vx: (Math.random() - 0.5) * 8,
        vy: Math.random() * 3 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 10 + 5,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        shape: Math.random() > 0.5 ? 'rect' : 'circle',
      });
    }

    let animationId: number;
    let startTime = Date.now();
    const duration = 4000; // 4 seconds

    const animate = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed > duration) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1; // gravity
        p.rotation += p.rotationSpeed;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;

        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-50"
      style={{ width: '100vw', height: '100vh' }}
    />
  );
}

interface ScanData {
  store: {
    name: string;
    logoUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
    successMessage: string | null;
  };
  campaign: {
    name: string;
    description: string | null;
    endDate: string;
  };
  runId: string;
  participatingStoresCount: number;
  subDescription: string | null;
}

interface FormErrors {
  name?: string;
  email?: string;
}

export default function ScanPage() {
  const { code } = useParams<{ code: string }>();
  const [data, setData] = useState<ScanData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ entries: number; successMessage: string | null; campaignName: string; campaignEndDate: string; runId: string; participatingStoresCount: number; participatingStores: ParticipatingStore[] } | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    loadScanData();
  }, [code]);

  const loadScanData = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: ScanData }>(`/scan/${code}`);
      setData(response.data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid QR code');
    } finally {
      setLoading(false);
    }
  };

  // Play celebration sound using Web Audio API
  const playCelebrationSound = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioContextRef.current;

      // Play a cheerful ascending arpeggio
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      notes.forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.15);

        gainNode.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.15);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.15 + 0.4);

        oscillator.start(ctx.currentTime + i * 0.15);
        oscillator.stop(ctx.currentTime + i * 0.15 + 0.4);
      });
    } catch (e) {
      // Audio not supported, fail silently
      console.log('Audio not supported');
    }
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    // Name validation
    if (!name.trim()) {
      errors.name = 'Name is required';
    } else if (name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(email.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Get platform info from navigator if available
      const platform = typeof navigator !== 'undefined' ? navigator.platform : null;

      const response = await apiClient.post<{ success: boolean; data: { entries: number; successMessage: string | null; runId: string; participatingStoresCount: number; participatingStores: ParticipatingStore[] } }>(
        `/scan/${code}/register`,
        { name: name.trim(), email: email.trim().toLowerCase(), platform }
      );

      // Play celebration sound and show confetti on success
      playCelebrationSound();
      setShowConfetti(true);

      setSuccess({
        entries: response.data.data.entries,
        successMessage: response.data.data.successMessage,
        campaignName: data?.campaign.name || '',
        campaignEndDate: data?.campaign.endDate || '',
        runId: response.data.data.runId,
        participatingStoresCount: response.data.data.participatingStoresCount,
        participatingStores: response.data.data.participatingStores || [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Oops!</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    const formattedEndDate = success.campaignEndDate
      ? new Date(success.campaignEndDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
      : '';
    const defaultMessage = formattedEndDate
      ? `You will be contacted by email if you won a prize on or about ${formattedEndDate}.`
      : 'You will be contacted by email if you won a prize.';
    const displayMessage = success.successMessage || defaultMessage;
    const otherStoresCount = success.participatingStoresCount - 1;

    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
        style={{ backgroundColor: data?.store.primaryColor || '#22c55e' }}
      >
        {showConfetti && <Confetti />}
        {/* Header with Logo and Title */}
        <div className="flex items-center gap-3 mb-6">
          <Logo size="lg" className="drop-shadow-lg" />
          <div className="text-white">
            <h2 className="text-xl font-bold leading-tight">Discover</h2>
            <h2 className="text-xl font-bold leading-tight">the Park</h2>
          </div>
        </div>

        {/* Campaign Name */}
        {success.campaignName && (
          <div className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <span className="text-white font-medium">{success.campaignName}</span>
          </div>
        )}

        <div className="text-center text-white max-w-md">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold mb-4">You're In!</h1>
          <p className="text-xl mb-4">
            You now have <span className="font-bold text-4xl">{success.entries}</span> {success.entries === 1 ? 'entry' : 'entries'} in the raffle!
          </p>
          <p className="text-white/90 mb-4">
            {displayMessage}
          </p>

          {/* Mini Map and Link to participating stores */}
          <div className="mt-6 pt-6 border-t border-white/20">
            <p className="text-white/90 mb-4">
              {otherStoresCount > 0
                ? 'For more chances to win, visit the other participating stores!'
                : 'View all participating stores and campaign details!'}
            </p>

            {/* Mini Map Preview - Clickable */}
            <Link
              to={`/campaign/${success.runId}`}
              className="block bg-white rounded-xl overflow-hidden shadow-lg mb-4 hover:shadow-xl transition-shadow max-w-sm mx-auto"
            >
              <div className="relative">
                <StoreMap
                  stores={success.participatingStores}
                  height="150px"
                  interactive={false}
                />
                {/* Overlay to make entire area clickable */}
                <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center">
                  <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-md">
                    <span className="text-xs font-medium text-gray-700 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Tap to view stores
                    </span>
                  </div>
                </div>
              </div>
            </Link>

            <Link
              to={`/campaign/${success.runId}`}
              className="inline-flex items-center gap-2 px-5 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              <MapPin className="w-5 h-5" />
              {otherStoresCount > 0
                ? `View ${otherStoresCount} Other Store${otherStoresCount !== 1 ? 's' : ''}`
                : 'Contest Details & Locations'}
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: data?.store.secondaryColor || '#f9fafb' }}>
      {/* Header with store branding */}
      <div
        className="p-6 text-center"
        style={{ backgroundColor: data?.store.primaryColor || '#22c55e' }}
      >
        {data?.store.logoUrl && (
          <img
            src={data.store.logoUrl}
            alt={data.store.name}
            className="h-16 mx-auto mb-4"
          />
        )}
        <h1 className="text-2xl font-bold text-white">{data?.store.name}</h1>
      </div>

      {/* Campaign info */}
      <div className="p-6 text-center">
        <h2 className="text-xl font-bold text-gray-900 mb-2">{data?.campaign.name}</h2>
        {data?.campaign.description && (
          <p className="text-gray-600 mb-2">{data.campaign.description}</p>
        )}
        {data?.subDescription && (
          <p className="text-gray-500 italic">{data.subDescription}</p>
        )}
      </div>

      {/* Registration form */}
      <div className="p-6 max-w-md mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
            Enter the Raffle!
          </h3>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Your Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (formErrors.name) setFormErrors(prev => ({ ...prev, name: undefined }));
                }}
                className={`input ${formErrors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="John Doe"
              />
              {formErrors.name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>

            <div>
              <label className="label">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (formErrors.email) setFormErrors(prev => ({ ...prev, email: undefined }));
                }}
                className={`input ${formErrors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="john@example.com"
              />
              {formErrors.email && (
                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 px-4 rounded-lg font-bold text-white transition-colors"
              style={{ backgroundColor: data?.store.primaryColor || '#22c55e' }}
            >
              {submitting ? 'Entering...' : 'Enter Raffle'}
            </button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-4">
            One entry per store per day. Visit more stores for more chances to win!
          </p>
        </div>
      </div>
    </div>
  );
}
